/**
 * Connector that resolves the default CarePlan for a patient-profile
 * Quick-Add and renders `CareTimeQuickAddModal`.
 *
 * Lives between `ModalProvider` (which owns the URL-hash open/close
 * state) and the presentational `CareTimeQuickAddModal`, mirroring the
 * `PhoneNumbersModalWrapper` pattern for provider-rendered modals that
 * need their own data fetch. Resolving the CarePlan here (rather than in
 * the provider) keeps care-time domain logic in the care-time folder.
 *
 * @agentic connections
 */

import { useEffect, useState } from "react";
import { useMedplum } from "@medplum/react";
import type { CarePlan } from "@medplum/fhirtypes";
import { findDefaultCarePlanForCareTime } from "@benchmark/core/services";
import { getCarePlanCategoryDisplay } from "@benchmark/core/constants";
import type { CareTimeMethod } from "@benchmark/core/constants";
import { CareTimeQuickAddModal } from "./CareTimeQuickAddModal";

/** Props for `CareTimeQuickAddModalWrapper`. @agentic connections */
export type CareTimeQuickAddModalWrapperProps = {
  opened: boolean;
  onClose: () => void;
  method: CareTimeMethod;
  patientId: string;
  patientDisplay: string;
  performerRoleId: string;
  performerDisplay: string;
};

/**
 * Resolves the default CarePlan and renders the Quick-Add modal.
 * @agentic connections
 */
export const CareTimeQuickAddModalWrapper = ({
  opened,
  onClose,
  method,
  patientId,
  patientDisplay,
  performerRoleId,
  performerDisplay,
}: CareTimeQuickAddModalWrapperProps) => {
  const medplum = useMedplum();
  // Cache the default CarePlan lookup result alongside the patientId
  // it was resolved for. Derivation at render time (rather than a
  // synchronous reset inside the effect) ensures we never flash a
  // stale `basedOn` reference into the modal when the effect re-fires
  // for a different patient.
  const [carePlanLookup, setCarePlanLookup] = useState<
    { patientId: string; plan: CarePlan | undefined } | undefined
  >(undefined);

  // Resolve the default CarePlan to anchor Quick-Add entries against
  // when the modal opens (A4: active CoCM CarePlan preferred, falling
  // back to the most recent active plan). Lazy: we only fetch when the
  // modal is open so we don't pay a FHIR read on profile load.
  // Soft-fails to `undefined` so a network blip never blocks Quick Add.
  useEffect(() => {
    if (!opened || !patientId) return;
    let cancelled = false;
    findDefaultCarePlanForCareTime(medplum, patientId)
      .then((plan) => {
        if (!cancelled) setCarePlanLookup({ patientId, plan });
      })
      .catch((err: unknown) => {
        console.error("Failed to resolve default CarePlan for Quick Add:", err);
        if (!cancelled) setCarePlanLookup({ patientId, plan: undefined });
      });
    return () => {
      cancelled = true;
    };
  }, [opened, patientId, medplum]);

  // Only honor the lookup result when it matches the current patient.
  // Anything else is stale (different patient resolved earlier) and
  // must not be forwarded to the Quick Add modal.
  const defaultCarePlan =
    carePlanLookup && carePlanLookup.patientId === patientId
      ? carePlanLookup.plan
      : undefined;

  const carePlanCategoryCode =
    defaultCarePlan?.category?.[0]?.coding?.[0]?.code;
  const defaultCarePlanDisplay =
    defaultCarePlan?.title ??
    (carePlanCategoryCode
      ? `${getCarePlanCategoryDisplay(carePlanCategoryCode)} Plan`
      : undefined);

  return (
    <CareTimeQuickAddModal
      opened={opened}
      onClose={onClose}
      method={method}
      patientId={patientId}
      patientDisplay={patientDisplay}
      performerRoleId={performerRoleId}
      performerDisplay={performerDisplay}
      carePlanId={defaultCarePlan?.id}
      carePlanDisplay={defaultCarePlanDisplay}
    />
  );
};
